import { UTSLocale } from "@smile2impress/attraction-constants";
import type { CmsPageContentProps } from "api/cms-website";

import type { HreflangAlternate } from "components/MetaTags/MetaTags.types";
import { UrlsConfig } from "configs/urlsConfig";
import {
    BASE_URL,
    I18nLocale,
    I18nLocaleToUTSLocale,
    StrapiLocale,
    StrapiLocaleToI18n,
} from "constants/enum";
import { getUTSLocale } from "utils/locale";
import {
    normalizePath,
    pathnameFromHref,
    removeLeadingLocalePrefixes,
} from "utils/localePathUtils";

const X_DEFAULT_LOCALE = I18nLocale.SPAIN_ES;

export const isCmsCatchAllPathname = (pathname: string): boolean =>
    pathname === UrlsConfig.CMS_PAGES || pathname === UrlsConfig.CMS_PREVIEW;

/**
 * Resolves a Next.js path (without locale prefix) for country switch on CMS pages.
 * Uses hreflang alternates built from Strapi localizations.
 */
export function getCmsRedirectPathFromAlternates(
    alternates: HreflangAlternate[] | null | undefined,
    targetLocale: I18nLocale,
    options?: { isPreview?: boolean }
): string | undefined {
    if (!alternates?.length) {
        return undefined;
    }

    const targetHrefLang = I18nLocaleToUTSLocale[targetLocale];
    const alternate = alternates.find((a) => a.hrefLang === targetHrefLang);
    if (!alternate?.href) {
        return undefined;
    }

    // Strip any leading locale prefix (the href may carry a foreign one) so the
    // caller's `router.push({ locale })` can't produce a `/fr/es/…` URL.
    const pathWithoutLocale = removeLeadingLocalePrefixes(
        normalizePath(pathnameFromHref(alternate.href))
    );

    if (options?.isPreview) {
        if (pathWithoutLocale === "/") {
            return "/cms-preview";
        }
        return `/cms-preview${pathWithoutLocale}`;
    }

    return pathWithoutLocale;
}

/**
 * Build hreflang alternate links for a CMS page (current locale + localizations).
 * hrefLang is UTSLocale (via getUTSLocale). Used so content managers' cross-links are reflected in <link rel="alternate" hreflang="..." />.
 */
export function buildCmsPageHreflangAlternates(
    content: CmsPageContentProps,
    currentLocale: string,
    currentSlug: string
): HreflangAlternate[] {
    const pathFromSlug = (slug: string) => (slug === "/" ? "" : slug);
    const href = (locale: string, slug: string) =>
        `${BASE_URL}/${locale}${pathFromSlug(slug)}`;

    const alternates: HreflangAlternate[] = [];
    const seen = new Set<UTSLocale | string>();

    const currentUTS = getUTSLocale(currentLocale);
    if (!seen.has(currentUTS)) {
        seen.add(currentUTS);
        alternates.push({
            href: href(currentLocale, currentSlug),
            hrefLang: currentUTS,
        });
    }

    content.localizations?.forEach((loc) => {
        const strapiLoc = loc.locale as unknown as StrapiLocale;
        const locI18n = StrapiLocaleToI18n[strapiLoc] as I18nLocale;
        const locUTS = getUTSLocale(locI18n);
        if (seen.has(locUTS)) return;
        seen.add(locUTS);
        alternates.push({
            href: href(locI18n, loc.slug),
            hrefLang: locUTS,
        });
    });

    const defaultHref =
        currentLocale === X_DEFAULT_LOCALE
            ? href(currentLocale, currentSlug)
            : (() => {
                  const esLoc = content.localizations?.find(
                      (loc) =>
                          StrapiLocaleToI18n[
                              loc.locale as unknown as StrapiLocale
                          ] === X_DEFAULT_LOCALE
                  );
                  return esLoc
                      ? href(X_DEFAULT_LOCALE, esLoc.slug)
                      : href(currentLocale, currentSlug);
              })();
    alternates.push({ href: defaultHref, hrefLang: "x-default" });

    return alternates;
}

/**
 * Build hreflang alternate links for a CMS page (current locale + localizations).
 * hrefLang is UTSLocale (via getUTSLocale). Used so content managers' cross-links are reflected in <link rel="alternate" hreflang="..." />.
 */
export function buildCmsBlogPostHreflangAlternates(
    content: Pick<CmsPageContentProps, "localizations">,
    currentLocale: string,
    currentSlug: string
): HreflangAlternate[] {
    const pathFromSlug = (slug: string) =>
        UrlsConfig.BLOG_POST.replace("[id]", slug);
    const href = (locale: string, slug: string) =>
        `${BASE_URL}/${locale}${pathFromSlug(slug)}`;

    const alternates: HreflangAlternate[] = [];
    const seen = new Set<UTSLocale | string>();

    const currentUTS = getUTSLocale(currentLocale);
    if (!seen.has(currentUTS)) {
        seen.add(currentUTS);
        alternates.push({
            href: href(currentLocale, currentSlug),
            hrefLang: currentUTS,
        });
    }

    content.localizations?.forEach((loc) => {
        const strapiLoc = loc.locale as unknown as StrapiLocale;
        const locI18n = StrapiLocaleToI18n[strapiLoc] as I18nLocale;
        const locUTS = getUTSLocale(locI18n);
        if (seen.has(locUTS)) return;
        seen.add(locUTS);
        alternates.push({
            href: href(locI18n, loc.slug),
            hrefLang: locUTS,
        });
    });

    const defaultHref =
        currentLocale === X_DEFAULT_LOCALE
            ? href(currentLocale, currentSlug)
            : (() => {
                  const esLoc = content.localizations?.find(
                      (loc) =>
                          StrapiLocaleToI18n[
                              loc.locale as unknown as StrapiLocale
                          ] === X_DEFAULT_LOCALE
                  );
                  return esLoc
                      ? href(X_DEFAULT_LOCALE, esLoc.slug)
                      : href(currentLocale, currentSlug);
              })();
    alternates.push({ href: defaultHref, hrefLang: "x-default" });

    return alternates;
}
